import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

const blankForm = { name: "", email: "", password: "", company_id: "", type: "INDIVIDUAL", bvn: "", nin: "" };

export default function RegisterPage() {
  const { registerCustomer } = useAuth();
  const [form, setForm] = useState(blankForm);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const onTypeChange = (t) => setForm((f) => ({
    ...f, type: t, bvn: t === "INDIVIDUAL" ? "" : f.bvn, nin: t === "CORPORATE" ? "" : f.nin,
  }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (!form.company_id) throw new Error("Please enter your Company ID");
      if (form.type === "INDIVIDUAL" && !/^\d{11}$/.test(form.nin)) throw new Error("NIN must be exactly 11 digits");
      if (form.type === "CORPORATE" && !/^\d{11}$/.test(form.bvn)) throw new Error("BVN must be exactly 11 digits");
      const payload = {
        ...form,
        bvn: form.type === "CORPORATE" ? form.bvn : null,
        nin: form.type === "INDIVIDUAL" ? form.nin : null,
      };
      await registerCustomer(payload);
      nav("/portal", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="register-page">
      <div className="hidden lg:flex relative flex-col justify-between p-12 text-white"
           style={{ background: "radial-gradient(circle at 30% 20%, #2C5545, #1C1C1B)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-white/10 backdrop-blur grid place-items-center">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="font-display font-semibold tracking-tight text-lg">KREPP</div>
        </div>
        <div className="max-w-md">
          <div className="label text-white/70 mb-4">Customer Self-Service</div>
          <h1 className="font-display font-light text-4xl xl:text-5xl leading-tight mb-6">
            Track your financial Platform, anytime.
          </h1>
          <p className="text-white/80 leading-relaxed">
            Sign up under your savings cooperative or finance company to view balances, repayment schedules, and pay seamlessly.
          </p>
        </div>
        <div className="text-white/60 text-xs tracking-[0.2em] uppercase">Need a company account? Ask your administrator.</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          <div className="label mb-2">Get started</div>
          <h2 className="font-display text-3xl font-semibold tracking-tight mb-8">Create your customer account</h2>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="label block mb-2">Customer Type *</label>
              <select required className={inputCls} value={form.type}
                      onChange={(e) => onTypeChange(e.target.value)} data-testid="register-type">
                <option value="INDIVIDUAL">Individual</option>
                <option value="CORPORATE">Corporate</option>
              </select>
            </div>
            <div>
              <label className="label block mb-2">Full Name</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className={inputCls} data-testid="register-name-input" />
            </div>
            <div>
              <label className="label block mb-2">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className={inputCls} data-testid="register-email-input" />
            </div>
            <div>
              <label className="label block mb-2">Password (min 8 characters)</label>
              <input type="password" required minLength={8} value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className={inputCls} data-testid="register-password-input" />
            </div>
            <div>
              <label className="label block mb-2">Company ID</label>
              <input type="text" required value={form.company_id} placeholder="Provided by your admin"
                     onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                     className={inputCls} data-testid="register-company-input" />
              <div className="text-xs text-muted-foreground mt-2">Don't know it? Contact your finance company administrator.</div>
            </div>

            {form.type === "INDIVIDUAL" ? (
              <div>
                <label className="label block mb-2">NIN * (11 digits)</label>
                <input required inputMode="numeric" pattern="\d{11}" maxLength={11} minLength={11}
                       className={inputCls} value={form.nin}
                       onChange={(e) => setForm({ ...form, nin: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                       data-testid="register-nin" placeholder="11-digit National Identification Number" />
                <div className="text-xs text-muted-foreground mt-1">Required for INDIVIDUAL customers.</div>
              </div>
            ) : (
              <div>
                <label className="label block mb-2">BVN * (11 digits)</label>
                <input required inputMode="numeric" pattern="\d{11}" maxLength={11} minLength={11}
                       className={inputCls} value={form.bvn}
                       onChange={(e) => setForm({ ...form, bvn: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                       data-testid="register-bvn" placeholder="11-digit Bank Verification Number" />
                <div className="text-xs text-muted-foreground mt-1">Required for CORPORATE customers.</div>
              </div>
            )}

            {err && <div className="text-sm text-secondary" data-testid="register-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full py-3" data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-forest font-medium hover:underline" data-testid="login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
